import React, { useEffect, useState } from 'react';
import './styles/EventDetailModal.css';
import { formatFirebaseTimestamp, displayEventSchedule } from '../../utils/datetime';
import { getEventCoverImage } from '../../api/EventsAPI';

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
);

const DetailRow = ({ label, value }) => (
    <div className="ed-detail-row">
        <span className="ed-detail-label">{label}</span>
        <span className="ed-detail-value">{value ?? '—'}</span>
    </div>
);

const SectionLabel = ({ children }) => (
    <div className="ed-section-label">{children}</div>
);

export default function EventDetailModal({ event, onClose }) {
    const [coverUrl, setCoverUrl] = useState(null);

    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleEsc);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    useEffect(() => {
        if (event?.id && event?.coverImage) {
            getEventCoverImage(event.id, event.coverImage)
                .then((res) => { if (res.success) setCoverUrl(res.imageUrl); })
                .catch(() => {});
        }
    }, [event?.id, event?.coverImage]);

    if (!event) return null;

    const isMeetup = event.connectionType === 'meetup';
    const isLive = !!event.isLive;

    return (
        <div className="ed-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ed-modal" role="dialog" aria-modal="true">
                <button className="ed-close" onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </button>

                {/* Cover image */}
                {coverUrl && (
                    <div className="ed-cover-wrap">
                        <img src={coverUrl} alt={event.title} className="ed-cover-img" />
                        <div className="ed-cover-overlay" />
                        <div className="ed-cover-badges">
                            {event.type && <span className="ed-badge type">{event.type}</span>}
                            <span className="ed-badge format">{event.connectionType || 'event'}</span>
                            <span className={`ed-badge live ${isLive ? 'active' : 'inactive'}`}>
                                {isLive ? 'Live' : 'Inactive'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className={`ed-header ${coverUrl ? 'has-cover' : ''}`}>
                    <div className="ed-title-row">
                        <div className="ed-title">{event.title}</div>
                        {!coverUrl && (
                            <div className="ed-header-badges">
                                {event.type && <span className="ed-badge type">{event.type}</span>}
                                <span className="ed-badge format">{event.connectionType || 'event'}</span>
                                <span className={`ed-badge live ${isLive ? 'active' : 'inactive'}`}>
                                    {isLive ? 'Live' : 'Inactive'}
                                </span>
                            </div>
                        )}
                    </div>
                    {event.host?.name && (
                        <div className="ed-host-row">
                            {event.host.photoURL && (
                                <img src={event.host.photoURL} alt={event.host.name} className="ed-host-avatar" />
                            )}
                            <span className="ed-host-name">Hosted by {event.host.name}</span>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="ed-body">
                    {/* Info block */}
                    {(event.info?.header || event.info?.paragraph) && (
                        <div className="ed-info-block">
                            {event.info.header && <p className="ed-info-header">{event.info.header}</p>}
                            {event.info.paragraph && <p className="ed-info-para">{event.info.paragraph}</p>}
                            {event.info.paragraph2 && <p className="ed-info-para">{event.info.paragraph2}</p>}
                        </div>
                    )}

                    {/* Details */}
                    <div className="ed-details">
                        <SectionLabel>Details</SectionLabel>
                        <DetailRow label="Event ID" value={<span className="ed-mono">{event.id}</span>} />
                        <DetailRow label="Date & time" value={displayEventSchedule(event)} />
                        <DetailRow label="Created" value={formatFirebaseTimestamp(event.createdAt)} />
                        {event.repeat && event.repeat !== 0 && (
                            <DetailRow label="Repeat" value={event.repeat} />
                        )}
                        {event.numReserved != null && (
                            <DetailRow label="Reserved" value={event.numReserved} />
                        )}
                    </div>

                    {/* Location */}
                    {event.location && (
                        <div className="ed-details" style={{ marginTop: '6px' }}>
                            <SectionLabel>Location</SectionLabel>
                            {event.location.name && <DetailRow label="Name" value={event.location.name} />}
                            {event.location.address && <DetailRow label="Address" value={event.location.address} />}
                            {event.location.latitude != null && (
                                <DetailRow label="Coordinates" value={`${event.location.latitude}, ${event.location.longitude}`} />
                            )}
                        </div>
                    )}

                    {/* Tags */}
                    {event.tags?.length > 0 && (
                        <div className="ed-details" style={{ marginTop: '6px' }}>
                            <SectionLabel>Tags</SectionLabel>
                            <div className="ed-tags-wrap">
                                {event.tags.map((tag, i) => (
                                    <span key={i} className="ed-tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tickets */}
                    {!isMeetup && event.ticketOptions?.length > 0 && (
                        <div className="ed-details" style={{ marginTop: '6px' }}>
                            <SectionLabel>Tickets</SectionLabel>
                            <div className="ed-tickets">
                                {event.ticketOptions.map((t, i) => (
                                    <div key={i} className="ed-ticket-card">
                                        <div className="ed-ticket-type">{t.type || 'General'}</div>
                                        <div className="ed-ticket-meta">
                                            <span>R{t.price}</span>
                                            <span>{t.numAvailable} available</span>
                                        </div>
                                        {t.desc && <div className="ed-ticket-desc">{t.desc}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
